import { useEffect, useState } from 'react';

import type { RejectTripRequestConfirmModalProps } from './useRejectTripRequestConfirmModalProps';
import { WaitAndReturnRejectModal } from './WaitAndReturnRejectModal';
import { useAppSelector } from 'src/app/store/hooks';
import { DeleteModal } from 'src/common/DeleteModal';
import { useWaitAndReturnSibling } from 'src/features/dispatch/modals/requestedTrips/useWaitAndReturnSibling';
import { selectEffectiveDispatchResponse } from 'src/features/dispatch/selectEffectiveDispatchResponse';

export const TripRejectRequestModal = ({
  props,
}: {
  props: RejectTripRequestConfirmModalProps;
}) => {
  const { waitAndReturnLegId, ...deleteModalProps } = props;

  // On the dispatch route both legs are in the dispatch response (with W&R flags
  // + correlation_id), so the sibling resolves synchronously and the normal
  // reject form never flashes before the W&R prompt.
  const unassignedTrips = useAppSelector(
    (state) => selectEffectiveDispatchResponse(state)?.unassigned_trips
  );

  // When a deferred accept is pending, this reject is already the "reject the
  // other leg" step of the accept prompt — don't re-offer the W&R reject prompt;
  // go straight to the reason form. (Detection is skipped entirely.)
  const acceptLegIdAfterReject = useAppSelector(
    (state) => state.dispatcher.modals.acceptLegIdAfterReject
  );

  const { sibling, isWaitAndReturnPair, isLoading } = useWaitAndReturnSibling(
    acceptLegIdAfterReject ? undefined : waitAndReturnLegId,
    unassignedTrips
  );

  // Tracks the "reject only this leg" choice, which falls through to the
  // standard single reject modal. Reset whenever the modal closes.
  const [rejectOnlyThisLeg, setRejectOnlyThisLeg] = useState(false);
  useEffect(() => {
    if (!props.open) {
      setRejectOnlyThisLeg(false);
    }
  }, [props.open]);

  // Prompt only when this leg is a W&R pair whose sibling is still awaiting a
  // decision (`requested`).
  const showWaitAndReturnPrompt =
    !rejectOnlyThisLeg &&
    isWaitAndReturnPair &&
    sibling?.status === 'requested';

  if (showWaitAndReturnPrompt && waitAndReturnLegId) {
    return (
      <WaitAndReturnRejectModal
        open={props.open}
        legTripId={waitAndReturnLegId}
        siblingId={sibling.id}
        onRejectOnlyThis={() => setRejectOnlyThisLeg(true)}
      />
    );
  }

  // On the async fallback (e.g. ROM all-requests), keep the confirm disabled
  // until detection resolves so a quick click can't reject a single leg and
  // skip the W&R prompt that's about to appear. Not while "reject only this" is
  // chosen (that's a deliberate fall-through). On the dispatch route detection
  // is synchronous, so isLoading is always false here.
  const isDetecting = !rejectOnlyThisLeg && isLoading;

  return (
    <DeleteModal
      {...deleteModalProps}
      loading={deleteModalProps.loading || isDetecting}
    />
  );
};
